'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface WhiteboardPanelProps {
  isVisible: boolean;
}

const WhiteboardPanel = ({ isVisible }: WhiteboardPanelProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [tool, setTool] = useState<'pen' | 'eraser' | 'text'>('pen');
  const [color, setColor] = useState('#2563EB');
  const [lineWidth, setLineWidth] = useState(3);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated || !isVisible) {
    return null;
  }

  const colors = [
    { name: 'Blue', value: '#2563EB' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Green', value: '#10B981' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Black', value: '#000000' },
    { name: 'White', value: '#FFFFFF' }
  ];

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  return (
    <div className="h-full flex flex-col bg-card rounded-lg border border-border">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="PencilSquareIcon" size={20} variant="outline" className="text-primary" />
          <h3 className="font-headline font-semibold text-foreground">Interactive Whiteboard</h3>
        </div>
        <button
          onClick={clearCanvas}
          className="px-3 py-1 text-sm font-medium text-error hover:bg-muted rounded-md transition-colors"
        >
          Clear All
        </button>
      </div>

      <div className="flex items-center space-x-4 p-4 border-b border-border bg-muted">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTool('pen')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'pen' ? 'bg-primary text-white' : 'bg-card text-foreground hover:bg-background'
            }`}
            aria-label="Pen tool"
          >
            <Icon name="PencilIcon" size={20} variant={tool === 'pen' ? 'solid' : 'outline'} />
          </button>
          <button
            onClick={() => setTool('eraser')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'eraser' ? 'bg-primary text-white' : 'bg-card text-foreground hover:bg-background'
            }`}
            aria-label="Eraser tool"
          >
            <Icon name="BackspaceIcon" size={20} variant={tool === 'eraser' ? 'solid' : 'outline'} />
          </button>
          <button
            onClick={() => setTool('text')}
            className={`p-2 rounded-md transition-colors ${
              tool === 'text' ? 'bg-primary text-white' : 'bg-card text-foreground hover:bg-background'
            }`}
            aria-label="Text tool"
          >
            <Icon name="ChatBubbleBottomCenterTextIcon" size={20} variant={tool === 'text' ? 'solid' : 'outline'} />
          </button>
        </div>

        <div className="h-8 w-px bg-border"></div>

        <div className="flex items-center space-x-2">
          {colors.map((c) => (
            <button
              key={c.value}
              onClick={() => setColor(c.value)}
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                color === c.value ? 'border-primary scale-110' : 'border-border hover:scale-105'
              }`}
              style={{ backgroundColor: c.value }}
              aria-label={`${c.name} color`}
            />
          ))}
        </div>

        <div className="h-8 w-px bg-border"></div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-muted-foreground">Size:</span>
          <input
            type="range"
            min="1"
            max="10"
            value={lineWidth}
            onChange={(e) => setLineWidth(parseInt(e.target.value))}
            className="w-24 h-2 bg-border rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #2563EB 0%, #2563EB ${(lineWidth / 10) * 100}%, #E2E8F0 ${(lineWidth / 10) * 100}%, #E2E8F0 100%)`
            }}
          />
          <span className="text-sm font-medium text-foreground min-w-[20px]">{lineWidth}</span>
        </div>
      </div>

      <div className="flex-1 p-4 bg-white">
        <canvas
          ref={canvasRef}
          width={800}
          height={600}
          className="w-full h-full border border-border rounded-lg cursor-crosshair"
        />
      </div>

      <div className="p-4 border-t border-border bg-muted">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="InformationCircleIcon" size={16} variant="outline" />
            <span>Click and drag to draw on the whiteboard</span>
          </div>
          <div className="flex items-center space-x-2">
            <button className="px-4 py-2 bg-card text-foreground border border-border rounded-md hover:bg-background transition-colors">
              Save
            </button>
            <button className="px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors">
              Share Screen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhiteboardPanel;